import { defineComponent as _defineComponent } from 'vue'
import { CreditRole } from '@/@types/graphql-types';
import TitleCreditName from '@/components/details/TitleCreditName.vue';
import TitleScoreList from '@/components/TitleScoreList.vue';
import { useI18n } from '@/helpers/composables/useI18n';

import { SnowplowContext, SnowplowTitleContext } from '@/helpers/tracking/providers';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { computed, ref } from 'vue';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { formatRuntime } from '@/helpers/title-helper';
import { getEffectiveJwRating } from '@/helpers/scoring-helper';
import { activeSubgenreCountries } from '@/constants/subgenres';
import { storeToRefs } from 'pinia';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';

type RedundantSubgenre = keyof typeof subgenreToGenreDictionary;
type Subgenre = TitleDetail['content']['subgenres'][number]['content'] & {
	url: { fullPath: string };
};


export default /*#__PURE__*/_defineComponent({
  __name: 'TitleDetailSideInfos',
  props: {
    title: null,
    hasStreamingChart: { type: Boolean, default: false },
    additionalContexts: { default: () => [] },
    itemLimit: null,
    itemStartAt: { default: 0 },
    aboveTheFold: { type: Boolean, default: false }
  },
  setup(__props: any) {

const props = __props as { title: TitleDetail, hasStreamingChart: boolean, additionalContexts: SnowplowContext[], itemLimit?: number, itemStartAt: number, aboveTheFold: boolean };



const { urlMetadata } = storeToRefs(useRoutingStore());
const { isPremium } = storeToRefs(useUserStore());
const { country } = storeToRefs(useLanguageStore());
const { t } = useI18n();

const subgenreToGenreDictionary = {
	'Mystery Thriller': 'Mystery & Thriller',
	'Action Adventure': 'Action & Adventure',
};

const titleContent = computed(() => props.title.content);

const scoring = computed(() => titleContent.value.scoring);

const htmlContent = computed(() => urlMetadata.value?.htmlContentJson ?? {});

const isReleased = computed(() => titleContent.value.isReleasedV2);

const jwVotes = computed(() => props.title.content.interactions?.votesNumber ?? null);

const directors = computed(() => {
	const credits =
		props.title.__typename === 'Season' ? props.title.show.content.directors : props.title.content.directors;

	return credits.filter(credit => credit.role === CreditRole.Director);
});
const trimDirectors = ref(directors.value.length > 5);

const runtime = computed(() => {
	const runtimeValue = titleContent.value.runtime!;
	const { translationKey, minutes, hours } = formatRuntime(runtimeValue);

	return t(translationKey, { minutes, hours }) as string;
});

const subgenres = computed(() => {
	return props.title.content.subgenres.map(({ content }) => content).filter(el => el.url) as Subgenre[];
});

const genres = computed(() => {
	const genresToRemove = new Set();
	subgenres.value.forEach(({ name }) => {
		if (subgenreToGenreDictionary[name as RedundantSubgenre] != null) {
			genresToRemove.add(subgenreToGenreDictionary[name as RedundantSubgenre]);
		}
	});

	return props.title.content.genres
		.map(genre => genre.translation)
		.filter(translation => !!translation && !genresToRemove.has(translation));
});

const showSubgenres = computed(() => {
	return activeSubgenreCountries.includes(country.value) && subgenres.value.length > 0;
});

const ageCertifications = computed(() => props.title.__typename !== 'Season' && props.title.content.ageCertification);

const productionCountries = computed(() => {
	const titleContent = props.title.__typename === 'Season' ? props.title.show.content : props.title.content;

	return titleContent.productionCountries?.map(country => t(`WEBAPP_COUNTRY_${country}`)).join(', ');
});

// the order is kept when using non-numeric keys.
const ITEMS = computed(() => ({
	// streamcharts
	directors: {
		subheadingTranslatable: 'WEBAPP_PERSONS_DIRECTOR',
		hasContent: directors.value.length > 0,
	},
	scores: {
		subheadingTranslatable: 'WEBAPP_RATING',
		hasContent:
			!htmlContent.value.hide_all_scorings &&
			(scoring.value.imdbScore || getEffectiveJwRating(scoring.value)) &&
			isReleased.value,
	},
	genres: {
		subheadingTranslatable: 'WEBAPP_GENRES',
		hasContent: genres.value.length > 0,
	},
	runtime: {
		subheadingTranslatable: 'WEBAPP_RUNTIME',
		hasContent: titleContent.value.runtime,
	},
	ageRating: {
		subheadingTranslatable: 'WEBAPP_AGERATING',
		hasContent: props.title.__typename !== 'Season' && props.title.content.ageCertification,
	},
	productionCountries: {
		subheadingTranslatable: 'WEBAPP_TITLE_DETAIL_PROD_COUNTRIES',
		hasContent: isPremium || productionCountries,
	},
	// (directors)
}));
const renderedKeys = computed<(keyof typeof ITEMS.value)[]>(() => {
	const itemStartAt = props.itemStartAt - (props.hasStreamingChart ? 2 : 0);
	return (
		(Object.keys(ITEMS.value) as (keyof typeof ITEMS.value)[])
			// filter out items that have no content (e.g. no directors, no scores, etc)
			.filter(key => ITEMS.value[key].hasContent)
			// start picking up items at `itemStartAt`, considering if title has streaming charts
			.filter((_key, index) => index >= itemStartAt)
			// limit to only `itemLimit`
			.filter((_key, index) => !props.itemLimit || index < props.itemLimit)
	);
});

function trackSubgenresClick(subgenre: string) {
	const seasonNumber = props.title.__typename === 'Season' ? props.title.content.seasonNumber : null;
	const contexts = (props.additionalContexts || []).concat([
		new SnowplowTitleContext(props.title.objectId, props.title.objectType, seasonNumber),
	]);

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'subgenre_clicked',
			label: subgenre,
		},
		contexts
	);
}

return { __sfc: true,props, urlMetadata, isPremium, country, t, subgenreToGenreDictionary, titleContent, scoring, htmlContent, isReleased, jwVotes, directors, trimDirectors, runtime, subgenres, genres, showSubgenres, ageCertifications, productionCountries, ITEMS, renderedKeys, trackSubgenresClick, TitleCreditName, TitleScoreList }
}

})